import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useIsMobile, useToasts } from '@brg/ui';
import { Designer } from '@brg/api';
import { LayoutConst, useModal } from 'core';
import { Date32Icon } from 'shared/components/tagAdminProjectsSearchBar/date32';
import { Date24Icon } from 'shared/components/tagAdminProjectsSearchBar/date24';
import { MagicBoxRow } from 'shared/components/MagicBoxRow';
import {
  IRegularDaySchedule,
  IRegularLessonData,
  LMSLessonClassTypeModel,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';
import { ClockButton } from 'shared/modals/LessonScheduleModals/ClockButton';
import { FieldLabel } from 'shared/modals/LessonScheduleModals/FieldLabel';
import {
  Chip,
  Chips,
  ClearDayButton,
  CloseButton,
  DaysControls,
  Dropdown,
  DropdownFixedField,
  Field,
  Header,
  HeaderRow,
  ModalDatePicker,
  ModalWrapper,
  Row,
  Subtitle,
  TextInput,
  TimeWrapper,
  Title,
  WeekdayButton,
  WeekdaysRow,
} from 'shared/modals/LessonScheduleModals/styles';


const emptyDaySchedule = (): IRegularDaySchedule => ({
  start: '',
  end: '',
  format: 'online',
  location: '',
  teacherId: '',
  isInvitedTeacher: false,
  selectedGroups: [],
});

const isDayFilled = (schedule?: IRegularDaySchedule) => Boolean(schedule?.start && schedule?.end);

const dropdownValue = (option: unknown) => {
  if (option == null) return '';
  if (typeof option === 'string' || typeof option === 'number') return String(option);
  if (typeof option === 'object' && 'value' in option) {
    return String((option as { value?: string | number }).value ?? '');
  }
  return '';
};

const snapshotDay = (values: {
  start?: string;
  end?: string;
  format?: unknown;
  location?: string;
  teacherId?: unknown;
  isInvitedTeacher?: boolean;
  selectedGroups?: string[];
}): IRegularDaySchedule => ({
  start: values.start || '',
  end: values.end || '',
  format: dropdownValue(values.format) === 'offline' ? 'offline' : 'online',
  location: values.location || '',
  teacherId: dropdownValue(values.teacherId),
  isInvitedTeacher: Boolean(values.isInvitedTeacher),
  selectedGroups: values.selectedGroups || [],
});

interface DayFormValues {
  courseStartDate: Date | null;
  courseEndDate: Date | null;
  selectedThemeId?: string;
  start: string;
  end: string;
  format: 'online' | 'offline';
  location: string;
  teacherId: string;
  isInvitedTeacher: boolean;
  selectedGroups: string[];
}

export interface RegularLessonModalProps {
  lessonType: LMSLessonClassTypeModel;
  lessonTypeLabel: string;
  courseStartDate: Date | null;
  courseEndDate: Date | null;
  courseGroups: Array<{ groupId: string; name: Array<{ lang: string; name: string }> }>;
  moduleTeachers: Designer<'Local'>[];
  themeOptions?: Array<{ value: string; label: string }>;
  getGroupName: (group: { groupId: string; name: Array<{ lang: string; name: string }> }) => string;
  isRecurring?: boolean;
  onSubmit: (data: IRegularLessonData, onSuccess: () => void) => void;
}

export const RegularLessonModal: FC<RegularLessonModalProps> = ({
  lessonType,
  lessonTypeLabel,
  courseStartDate,
  courseEndDate,
  courseGroups,
  moduleTeachers,
  themeOptions = [],
  getGroupName,
  isRecurring = false,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();
  const isMobile = useIsMobile();
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);

  const initialDay = courseStartDate instanceof Date && !Number.isNaN(courseStartDate.getTime())
    ? courseStartDate.getDay()
    : 1;

  const [activeDay, setActiveDay] = useState(initialDay);
  const [daySchedules, setDaySchedules] = useState<Record<number, IRegularDaySchedule>>({});
  const [dropdownResetKey, setDropdownResetKey] = useState(0);
  const daySchedulesRef = useRef<Record<number, IRegularDaySchedule>>({});

  const { control, watch, setValue, getValues } = useForm<DayFormValues>({
    defaultValues: {
      courseStartDate,
      courseEndDate,
      ...emptyDaySchedule(),
    },
  });

  const formData = watch();

  const weekdayOptions = useMemo(() => [
    { value: 1, label: t('lms.lessonsEditor.weekDays.mon') },
    { value: 2, label: t('lms.lessonsEditor.weekDays.tue') },
    { value: 3, label: t('lms.lessonsEditor.weekDays.wed') },
    { value: 4, label: t('lms.lessonsEditor.weekDays.thu') },
    { value: 5, label: t('lms.lessonsEditor.weekDays.fri') },
    { value: 6, label: t('lms.lessonsEditor.weekDays.sat') },
    { value: 0, label: t('lms.lessonsEditor.weekDays.sun') },
  ], [t]);

  const formatOptions = useMemo(
    () => [
      { value: 'online', label: t('lms.consultation.online') },
      { value: 'offline', label: t('lms.consultation.offline') },
    ],
    [t],
  );

  const teacherOptions = useMemo(
    () => moduleTeachers.map((teacher) => ({
      value: teacher.designerId,
      label: [teacher.surName, teacher.name, teacher.middleName].filter(Boolean).join(' '),
    })),
    [moduleTeachers],
  );

  const persistActiveDay = useCallback(() => {
    const next = {
      ...daySchedulesRef.current,
      [activeDay]: snapshotDay(getValues()),
    };
    daySchedulesRef.current = next;
    setDaySchedules(next);
  }, [activeDay, getValues]);

  const applyDaySchedule = useCallback((schedule: IRegularDaySchedule) => {
    setValue('start', schedule.start);
    setValue('end', schedule.end);
    setValue('format', schedule.format);
    setValue('location', schedule.location);
    setValue('teacherId', schedule.teacherId);
    setValue('isInvitedTeacher', schedule.isInvitedTeacher);
    setValue('selectedGroups', schedule.selectedGroups);
    setDropdownResetKey((key) => key + 1);
  }, [setValue]);

  const selectDay = useCallback((day: number) => {
    if (day === activeDay) return;
    persistActiveDay();
    setActiveDay(day);
    applyDaySchedule(daySchedulesRef.current[day] || emptyDaySchedule());
  }, [activeDay, applyDaySchedule, persistActiveDay]);

  const clearActiveDay = useCallback(() => {
    const next = { ...daySchedulesRef.current };
    delete next[activeDay];
    daySchedulesRef.current = next;
    setDaySchedules(next);
    applyDaySchedule(emptyDaySchedule());
  }, [activeDay, applyDaySchedule]);

  const toggleGroup = useCallback((groupId: string) => {
    const selected = watch('selectedGroups') || [];
    setValue(
      'selectedGroups',
      selected.includes(groupId) ? selected.filter((id) => id !== groupId) : [...selected, groupId],
    );
  }, [setValue, watch]);

  const filledDays = useMemo(() => {
    const next = { ...daySchedules };
    next[activeDay] = {
      start: formData.start || '',
      end: formData.end || '',
      format: formData.format || 'online',
      location: formData.location || '',
      teacherId: formData.teacherId || '',
      isInvitedTeacher: Boolean(formData.isInvitedTeacher),
      selectedGroups: formData.selectedGroups || [],
    };
    return Object.entries(next)
      .filter(([, schedule]) => isDayFilled(schedule))
      .map(([day]) => Number(day));
  }, [activeDay, daySchedules, formData]);

  const submit = useCallback(() => {
    persistActiveDay();

    const values = getValues();
    const schedules: Record<number, IRegularDaySchedule> = {
      ...daySchedulesRef.current,
      [activeDay]: snapshotDay(values),
    };

    let completeSchedules = Object.fromEntries(
      Object.entries(schedules).filter(([, schedule]) => isDayFilled(schedule)),
    ) as Record<number, IRegularDaySchedule>;

    if (!values.courseStartDate) {
      toasts.error(t('lms.lessonsEditor.enterStartDateError'));
      return;
    }

    if (isRecurring && !values.courseEndDate) {
      toasts.error(t('lms.lessonsEditor.enterEndDateError'));
      return;
    }

    if (!isRecurring) {
      const activeSchedule = schedules[activeDay];
      if (!isDayFilled(activeSchedule)) {
        toasts.error(t('lms.lessonsEditor.enterTimeError'));
        return;
      }
      completeSchedules = { [activeDay]: activeSchedule };
    }

    if (Object.keys(completeSchedules).length === 0) {
      toasts.error(t('lms.lessonsEditor.selectDayError'));
      return;
    }

    const invalidTime = Object.values(completeSchedules).some(
      (schedule) => schedule.end && schedule.start && schedule.end < schedule.start,
    );
    if (invalidTime) {
      toasts.error(t('lms.lessonsEditor.endTimeBeforeStartError'));
      return;
    }

    let periodStart = values.courseStartDate;
    let periodEnd = isRecurring
      ? values.courseEndDate
      : new Date(values.courseStartDate.getTime());

    if (!isRecurring) {
      const aligned = new Date(values.courseStartDate.getTime());
      const diff = (activeDay - aligned.getDay() + 7) % 7;
      aligned.setDate(aligned.getDate() + diff);
      periodStart = aligned;
      periodEnd = new Date(aligned.getTime());
    }

    if (!isRecurring && !values.selectedThemeId) {
      toasts.error(t('lms.lessonsEditor.selectThemeError'));
      return;
    }

    const hasMissingDayFields = Object.values(completeSchedules).some(
      (schedule) => !schedule.start
        || !schedule.end
        || !schedule.format
        || !schedule.location?.trim()
        || !schedule.teacherId,
    );
    if (hasMissingDayFields) {
      toasts.error(t('lms.consultation.fillRequiredFields'));
      return;
    }

    onSubmit(
      {
        courseStartDate: periodStart,
        courseEndDate: periodEnd,
        lessonType,
        daySchedules: completeSchedules,
        selectedThemeId: values.selectedThemeId,
      },
      () => modal.hide(),
    );
  }, [activeDay, getValues, isRecurring, lessonType, modal, onSubmit, persistActiveDay, t, toasts]);

  return (
    <ModalWrapper>
      <Header>
        <HeaderRow>
          <Title>{t('lms.lessonsEditor.regularLessonTitle', { type: lessonTypeLabel })}</Title>
          <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
        </HeaderRow>
        <Subtitle>{t('lms.consultation.subtitle')}</Subtitle>
      </Header>

      {isRecurring ? (
        <Row>
          <Field>
            <FieldLabel required>{t('lms.lessonsEditor.startDateLabel')}</FieldLabel>
            <Controller name="courseStartDate" control={control} render={({ field }) => (
              <ModalDatePicker
                // @ts-ignore
                value={field.value}
                onChange={(date: Date | null) => field.onChange(date)}
                format="dd.MM.yyyy"
                calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                clearIcon={null}
                locale={i18n.language}
              />
            )} />
          </Field>
          <Field>
            <FieldLabel required>{t('lms.lessonsEditor.endDateLabel')}</FieldLabel>
            <Controller name="courseEndDate" control={control} render={({ field }) => (
              <ModalDatePicker
                // @ts-ignore
                value={field.value}
                onChange={(date: Date | null) => field.onChange(date)}
                format="dd.MM.yyyy"
                calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                clearIcon={null}
                locale={i18n.language}
                minDate={formData.courseStartDate || undefined}
              />
            )} />
          </Field>
        </Row>
      ) : (
        <Row>
          <DropdownFixedField>
            <FieldLabel required>{t('lms.lessonsEditor.chooseTheme')}</FieldLabel>
            <Dropdown>
              <Controller
                name="selectedThemeId"
                control={control}
                render={({ field }) => (
                  <CommonInput
                    type={INPUT_TYPES.dropdown}
                    orientation="column"
                    options={themeOptions}
                    value={themeOptions.find((option) => option.value === field.value)}
                    onChange={(option: any) => field.onChange(option?.value)}
                    placeholder={t('lms.lessonsEditor.chooseTheme')}
                  />
                )}
              />
            </Dropdown>
          </DropdownFixedField>
          <Field>
            <FieldLabel required>{t('lms.lessonsEditor.lessonDateLabel')}</FieldLabel>
            <Controller name="courseStartDate" control={control} render={({ field }) => (
              <ModalDatePicker
                // @ts-ignore
                value={field.value}
                onChange={(date: Date | null) => {
                  field.onChange(date);
                  setValue('courseEndDate', date ? new Date(date.getTime()) : null);
                }}
                format="dd.MM.yyyy"
                calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                clearIcon={null}
                locale={i18n.language}
              />
            )} />
          </Field>
        </Row>
      )}

      <DaysControls>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.selectDayLabel')}</FieldLabel>
          <WeekdaysRow>
            {weekdayOptions.map((day) => (
              <WeekdayButton
                key={day.value}
                type="button"
                $active={activeDay === day.value}
                $filled={filledDays.includes(day.value) && activeDay !== day.value}
                onClick={() => selectDay(day.value)}
              >
                {day.label}
              </WeekdayButton>
            ))}
          </WeekdaysRow>
        </Field>
        <ClearDayButton type="button" onClick={clearActiveDay}>
          {t('lms.consultation.clearDay', {
            day: weekdayOptions.find((day) => day.value === activeDay)?.label,
          })}
        </ClearDayButton>
      </DaysControls>

      <Row>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.startTimeLabel')}</FieldLabel>
          <TimeWrapper>
            <Controller name="start" control={control} render={({ field }) => (
              <TextInput
                ref={(element) => {
                  field.ref(element);
                  startRef.current = element;
                }}
                type="time"
                value={field.value}
                onChange={field.onChange}
              />
            )} />
            <ClockButton onClick={() => startRef.current?.showPicker?.()} />
          </TimeWrapper>
        </Field>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.endTimeLabel')}</FieldLabel>
          <TimeWrapper>
            <Controller name="end" control={control} render={({ field }) => (
              <TextInput
                ref={(element) => {
                  field.ref(element);
                  endRef.current = element;
                }}
                type="time"
                min={formData.start || undefined}
                value={field.value}
                onChange={field.onChange}
              />
            )} />
            <ClockButton onClick={() => endRef.current?.showPicker?.()} />
          </TimeWrapper>
        </Field>
      </Row>

      <Row>
        <Field>
          <FieldLabel required>{t('lms.consultation.format')}</FieldLabel>
          <Dropdown>
            <Controller name="format" control={control} render={({ field }) => (
              <CommonInput
                key={`format-${activeDay}-${dropdownResetKey}`}
                type={INPUT_TYPES.dropdown}
                orientation="column"
                options={formatOptions}
                value={formatOptions.find((option) => option.value === field.value)}
                onChange={(option: any) => field.onChange(dropdownValue(option) || 'online')}
              />
            )} />
          </Dropdown>
        </Field>
        <Field>
          <FieldLabel required>
            {formData.format === 'online'
              ? t('lms.consultation.lessonLink')
              : t('lms.consultation.room')}
          </FieldLabel>
          <Controller name="location" control={control} render={({ field }) => (
            <TextInput placeholder={t('lms.lessonsEditor.enterData')} {...field} />
          )} />
        </Field>
      </Row>

      <Field>
        <FieldLabel required>{t('lms.consultation.teacher')}</FieldLabel>
        <Dropdown>
          <Controller name="teacherId" control={control} render={({ field }) => (
            <CommonInput
              key={`teacher-${activeDay}-${dropdownResetKey}`}
              type={INPUT_TYPES.dropdown}
              orientation="column"
              options={teacherOptions}
              value={teacherOptions.find((option) => option.value === field.value)}
              onChange={(option: any) => field.onChange(dropdownValue(option))}
              placeholder={t('lms.lessonsEditor.chooseTeacher')}
            />
          )} />
        </Dropdown>
        <Controller name="isInvitedTeacher" control={control} render={({ field }) => (
          <MagicBoxRow
            className="checkbox module_checkbox"
            checked={field.value}
            label={t('lms.consultation.invitedTeacher')}
            onClick={() => field.onChange(!field.value)}
            isTeamEdit
          />
        )} />
      </Field>

      <Field>
        <FieldLabel>{t('lms.lessonsEditor.groupsLabel')}</FieldLabel>
        <Chips>
          {courseGroups.map((group) => {
            const isSelected = (formData.selectedGroups || []).includes(group.groupId);
            return (
              <Chip
                key={group.groupId}
                type="button"
                $selected={isSelected}
                aria-pressed={isSelected}
                onClick={() => toggleGroup(group.groupId)}
              >
                <span>{getGroupName(group)}</span>
              </Chip>
            );
          })}
        </Chips>
      </Field>

      <FlexBox justify="flex-end">
        <BrgButton fullWidth variant={ButtonVariant.Filled} onClick={submit}>
          {t('lms.lessonsEditor.addLessonsToSchedule')}
        </BrgButton>
      </FlexBox>
    </ModalWrapper>
  );
};
import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { BrgButton, ButtonVariant, CommonInput, FlexBox, INPUT_TYPES, useBreakpoint, useIsMobile, useToasts } from '@brg/ui';
import { Designer } from '@brg/api';
import { LayoutConst, useModal } from 'core';
import { Date32Icon } from 'shared/components/tagAdminProjectsSearchBar/date32';
import { Date24Icon } from 'shared/components/tagAdminProjectsSearchBar/date24';
import { MagicBoxRow } from 'shared/components/MagicBoxRow';
import {
  IRegularDaySchedule,
  IRegularLessonData,
  LMSLessonClassTypeModel,
} from 'shared/pages/projectEdit/components/LMS/LessonsEditor/LessonsEditorTypes';
import { ClockButton } from 'shared/modals/LessonScheduleModals/ClockButton';
import { FieldLabel } from 'shared/modals/LessonScheduleModals/FieldLabel';
import {
  Chip,
  Chips,
  ClearDayButton,
  CloseButton,
  DaysControls,
  Dropdown,
  DropdownFixedField,
  Field,
  Header,
  HeaderRow,
  ModalDatePicker,
  ModalWrapper,
  Row,
  Subtitle,
  TextInput,
  TimeWrapper,
  Title,
  WeekdayButton,
  WeekdaysRow,
} from 'shared/modals/LessonScheduleModals/styles';


const emptyDaySchedule = (): IRegularDaySchedule => ({
  start: '',
  end: '',
  format: 'online',
  location: '',
  teacherId: '',
  isInvitedTeacher: false,
  selectedGroups: [],
});

const isDayFilled = (schedule?: IRegularDaySchedule) => Boolean(schedule?.start && schedule?.end);

const dropdownValue = (option: unknown) => {
  if (option == null) return '';
  if (typeof option === 'string' || typeof option === 'number') return String(option);
  if (typeof option === 'object' && 'value' in option) {
    return String((option as { value?: string | number }).value ?? '');
  }
  return '';
};

const snapshotDay = (values: {
  start?: string;
  end?: string;
  format?: unknown;
  location?: string;
  teacherId?: unknown;
  isInvitedTeacher?: boolean;
  selectedGroups?: string[];
}): IRegularDaySchedule => ({
  start: values.start || '',
  end: values.end || '',
  format: dropdownValue(values.format) === 'offline' ? 'offline' : 'online',
  location: values.location || '',
  teacherId: dropdownValue(values.teacherId),
  isInvitedTeacher: Boolean(values.isInvitedTeacher),
  selectedGroups: values.selectedGroups || [],
});

interface DayFormValues {
  courseStartDate: Date | null;
  courseEndDate: Date | null;
  selectedThemeId?: string;
  start: string;
  end: string;
  format: 'online' | 'offline';
  location: string;
  teacherId: string;
  isInvitedTeacher: boolean;
  selectedGroups: string[];
}

export interface RegularLessonModalProps {
  lessonType: LMSLessonClassTypeModel;
  lessonTypeLabel: string;
  courseStartDate: Date | null;
  courseEndDate: Date | null;
  courseGroups: Array<{ groupId: string; name: Array<{ lang: string; name: string }> }>;
  moduleTeachers: Designer<'Local'>[];
  themeOptions?: Array<{ value: string; label: string }>;
  getGroupName: (group: { groupId: string; name: Array<{ lang: string; name: string }> }) => string;
  isRecurring?: boolean;
  onSubmit: (data: IRegularLessonData, onSuccess: () => void) => void;
}

export const RegularLessonModal: FC<RegularLessonModalProps> = ({
  lessonType,
  lessonTypeLabel,
  courseStartDate,
  courseEndDate,
  courseGroups,
  moduleTeachers,
  themeOptions = [],
  getGroupName,
  isRecurring = false,
  onSubmit,
}) => {
  const { t, i18n } = useTranslation();
  const modal = useModal();
  const toasts = useToasts();
  const isMobile = useIsMobile();
  const isMac13 = useBreakpoint(LayoutConst.screen.mac13);
  const startRef = useRef<HTMLInputElement | null>(null);
  const endRef = useRef<HTMLInputElement | null>(null);

  const initialDay = courseStartDate instanceof Date && !Number.isNaN(courseStartDate.getTime())
    ? courseStartDate.getDay()
    : 1;

  const [activeDay, setActiveDay] = useState(initialDay);
  const [daySchedules, setDaySchedules] = useState<Record<number, IRegularDaySchedule>>({});
  const [dropdownResetKey, setDropdownResetKey] = useState(0);
  const daySchedulesRef = useRef<Record<number, IRegularDaySchedule>>({});

  const { control, watch, setValue, getValues } = useForm<DayFormValues>({
    defaultValues: {
      courseStartDate,
      courseEndDate,
      ...emptyDaySchedule(),
    },
  });

  const formData = watch();

  const weekdayOptions = useMemo(() => [
    { value: 1, label: t('lms.lessonsEditor.weekDays.mon') },
    { value: 2, label: t('lms.lessonsEditor.weekDays.tue') },
    { value: 3, label: t('lms.lessonsEditor.weekDays.wed') },
    { value: 4, label: t('lms.lessonsEditor.weekDays.thu') },
    { value: 5, label: t('lms.lessonsEditor.weekDays.fri') },
    { value: 6, label: t('lms.lessonsEditor.weekDays.sat') },
    { value: 0, label: t('lms.lessonsEditor.weekDays.sun') },
  ], [t]);

  const formatOptions = useMemo(
    () => [
      { value: 'online', label: t('lms.consultation.online') },
      { value: 'offline', label: t('lms.consultation.offline') },
    ],
    [t],
  );

  const teacherOptions = useMemo(
    () => moduleTeachers.map((teacher) => ({
      value: teacher.designerId,
      label: [teacher.surName, teacher.name, teacher.middleName].filter(Boolean).join(' '),
    })),
    [moduleTeachers],
  );

  const persistActiveDay = useCallback(() => {
    const next = {
      ...daySchedulesRef.current,
      [activeDay]: snapshotDay(getValues()),
    };
    daySchedulesRef.current = next;
    setDaySchedules(next);
  }, [activeDay, getValues]);

  const applyDaySchedule = useCallback((schedule: IRegularDaySchedule) => {
    setValue('start', schedule.start);
    setValue('end', schedule.end);
    setValue('format', schedule.format);
    setValue('location', schedule.location);
    setValue('teacherId', schedule.teacherId);
    setValue('isInvitedTeacher', schedule.isInvitedTeacher);
    setValue('selectedGroups', schedule.selectedGroups);
    setDropdownResetKey((key) => key + 1);
  }, [setValue]);

  const selectDay = useCallback((day: number) => {
    if (day === activeDay) return;
    persistActiveDay();
    setActiveDay(day);
    applyDaySchedule(daySchedulesRef.current[day] || emptyDaySchedule());
  }, [activeDay, applyDaySchedule, persistActiveDay]);

  const clearActiveDay = useCallback(() => {
    const next = { ...daySchedulesRef.current };
    delete next[activeDay];
    daySchedulesRef.current = next;
    setDaySchedules(next);
    applyDaySchedule(emptyDaySchedule());
  }, [activeDay, applyDaySchedule]);

  const toggleGroup = useCallback((groupId: string) => {
    const selected = watch('selectedGroups') || [];
    setValue(
      'selectedGroups',
      selected.includes(groupId) ? selected.filter((id) => id !== groupId) : [...selected, groupId],
    );
  }, [setValue, watch]);

  const filledDays = useMemo(() => {
    const next = { ...daySchedules };
    next[activeDay] = {
      start: formData.start || '',
      end: formData.end || '',
      format: formData.format || 'online',
      location: formData.location || '',
      teacherId: formData.teacherId || '',
      isInvitedTeacher: Boolean(formData.isInvitedTeacher),
      selectedGroups: formData.selectedGroups || [],
    };
    return Object.entries(next)
      .filter(([, schedule]) => isDayFilled(schedule))
      .map(([day]) => Number(day));
  }, [activeDay, daySchedules, formData]);

  const submit = useCallback(() => {
    persistActiveDay();

    const values = getValues();
    const schedules: Record<number, IRegularDaySchedule> = {
      ...daySchedulesRef.current,
      [activeDay]: snapshotDay(values),
    };

    let completeSchedules = Object.fromEntries(
      Object.entries(schedules).filter(([, schedule]) => isDayFilled(schedule)),
    ) as Record<number, IRegularDaySchedule>;

    if (!values.courseStartDate) {
      toasts.error(t('lms.lessonsEditor.enterStartDateError'));
      return;
    }

    if (isRecurring && !values.courseEndDate) {
      toasts.error(t('lms.lessonsEditor.enterEndDateError'));
      return;
    }

    if (!isRecurring) {
      const activeSchedule = schedules[activeDay];
      if (!isDayFilled(activeSchedule)) {
        toasts.error(t('lms.lessonsEditor.enterTimeError'));
        return;
      }
      completeSchedules = { [activeDay]: activeSchedule };
    }

    if (Object.keys(completeSchedules).length === 0) {
      toasts.error(t('lms.lessonsEditor.selectDayError'));
      return;
    }

    const invalidTime = Object.values(completeSchedules).some(
      (schedule) => schedule.end && schedule.start && schedule.end < schedule.start,
    );
    if (invalidTime) {
      toasts.error(t('lms.lessonsEditor.endTimeBeforeStartError'));
      return;
    }

    let periodStart = values.courseStartDate;
    let periodEnd = isRecurring
      ? values.courseEndDate
      : new Date(values.courseStartDate.getTime());

    if (!isRecurring) {
      const aligned = new Date(values.courseStartDate.getTime());
      const diff = (activeDay - aligned.getDay() + 7) % 7;
      aligned.setDate(aligned.getDate() + diff);
      periodStart = aligned;
      periodEnd = new Date(aligned.getTime());
    }

    if (!isRecurring && !values.selectedThemeId) {
      toasts.error(t('lms.lessonsEditor.selectThemeError'));
      return;
    }

    const hasMissingDayFields = Object.values(completeSchedules).some(
      (schedule) => !schedule.start
        || !schedule.end
        || !schedule.format
        || !schedule.location?.trim()
        || !schedule.teacherId,
    );
    if (hasMissingDayFields) {
      toasts.error(t('lms.consultation.fillRequiredFields'));
      return;
    }

    onSubmit(
      {
        courseStartDate: periodStart,
        courseEndDate: periodEnd,
        lessonType,
        daySchedules: completeSchedules,
        selectedThemeId: values.selectedThemeId,
      },
      () => modal.hide(),
    );
  }, [activeDay, getValues, isRecurring, lessonType, modal, onSubmit, persistActiveDay, t, toasts]);

  return (
    <ModalWrapper>
      <Header>
        <HeaderRow>
          <Title>{t('lms.lessonsEditor.regularLessonTitle', { type: lessonTypeLabel })}</Title>
          <CloseButton type="button" onClick={() => modal.hide()} aria-label="close">×</CloseButton>
        </HeaderRow>
        <Subtitle>{t('lms.consultation.subtitle')}</Subtitle>
      </Header>

      {isRecurring ? (
        <Row>
          <Field>
            <FieldLabel required>{t('lms.lessonsEditor.startDateLabel')}</FieldLabel>
            <Controller name="courseStartDate" control={control} render={({ field }) => (
              <ModalDatePicker
                // @ts-ignore
                value={field.value}
                onChange={(date: Date | null) => field.onChange(date)}
                format="dd.MM.yyyy"
                calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                clearIcon={null}
                locale={i18n.language}
              />
            )} />
          </Field>
          <Field>
            <FieldLabel required>{t('lms.lessonsEditor.endDateLabel')}</FieldLabel>
            <Controller name="courseEndDate" control={control} render={({ field }) => (
              <ModalDatePicker
                // @ts-ignore
                value={field.value}
                onChange={(date: Date | null) => field.onChange(date)}
                format="dd.MM.yyyy"
                calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                clearIcon={null}
                locale={i18n.language}
                minDate={formData.courseStartDate || undefined}
              />
            )} />
          </Field>
        </Row>
      ) : (
        <Row>
          <DropdownFixedField>
            <FieldLabel required>{t('lms.lessonsEditor.chooseTheme')}</FieldLabel>
            <Dropdown>
              <Controller
                name="selectedThemeId"
                control={control}
                render={({ field }) => (
                  <CommonInput
                    type={INPUT_TYPES.dropdown}
                    orientation="column"
                    options={themeOptions}
                    value={themeOptions.find((option) => option.value === field.value)}
                    onChange={(option: any) => field.onChange(option?.value)}
                    placeholder={t('lms.lessonsEditor.chooseTheme')}
                  />
                )}
              />
            </Dropdown>
          </DropdownFixedField>
          <Field>
            <FieldLabel required>{t('lms.lessonsEditor.lessonDateLabel')}</FieldLabel>
            <Controller name="courseStartDate" control={control} render={({ field }) => (
              <ModalDatePicker
                // @ts-ignore
                value={field.value}
                onChange={(date: Date | null) => {
                  field.onChange(date);
                  setValue('courseEndDate', date ? new Date(date.getTime()) : null);
                }}
                format="dd.MM.yyyy"
                calendarIcon={isMobile || isMac13 ? <Date24Icon /> : <Date32Icon />}
                clearIcon={null}
                locale={i18n.language}
              />
            )} />
          </Field>
        </Row>
      )}

      <DaysControls>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.selectDayLabel')}</FieldLabel>
          <WeekdaysRow>
            {weekdayOptions.map((day) => (
              <WeekdayButton
                key={day.value}
                type="button"
                $active={activeDay === day.value}
                $filled={filledDays.includes(day.value) && activeDay !== day.value}
                onClick={() => selectDay(day.value)}
              >
                {day.label}
              </WeekdayButton>
            ))}
          </WeekdaysRow>
        </Field>
        <ClearDayButton type="button" onClick={clearActiveDay}>
          {t('lms.consultation.clearDay', {
            day: weekdayOptions.find((day) => day.value === activeDay)?.label,
          })}
        </ClearDayButton>
      </DaysControls>

      <Row>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.startTimeLabel')}</FieldLabel>
          <TimeWrapper>
            <Controller name="start" control={control} render={({ field }) => (
              <TextInput
                ref={(element) => {
                  field.ref(element);
                  startRef.current = element;
                }}
                type="time"
                value={field.value}
                onChange={field.onChange}
              />
            )} />
            <ClockButton onClick={() => startRef.current?.showPicker?.()} />
          </TimeWrapper>
        </Field>
        <Field>
          <FieldLabel required>{t('lms.lessonsEditor.endTimeLabel')}</FieldLabel>
          <TimeWrapper>
            <Controller name="end" control={control} render={({ field }) => (
              <TextInput
                ref={(element) => {
                  field.ref(element);
                  endRef.current = element;
                }}
                type="time"
                min={formData.start || undefined}
                value={field.value}
                onChange={field.onChange}
              />
            )} />
            <ClockButton onClick={() => endRef.current?.showPicker?.()} />
          </TimeWrapper>
        </Field>
      </Row>

      <Row>
        <Field>
          <FieldLabel required>{t('lms.consultation.format')}</FieldLabel>
          <Dropdown>
            <Controller name="format" control={control} render={({ field }) => (
              <CommonInput
                key={`format-${activeDay}-${dropdownResetKey}`}
                type={INPUT_TYPES.dropdown}
                orientation="column"
                options={formatOptions}
                value={formatOptions.find((option) => option.value === field.value)}
                onChange={(option: any) => field.onChange(dropdownValue(option) || 'online')}
              />
            )} />
          </Dropdown>
        </Field>
        <Field>
          <FieldLabel required>
            {formData.format === 'online'
              ? t('lms.consultation.lessonLink')
              : t('lms.consultation.room')}
          </FieldLabel>
          <Controller name="location" control={control} render={({ field }) => (
            <TextInput placeholder={t('lms.lessonsEditor.enterData')} {...field} />
          )} />
        </Field>
      </Row>

      <Field>
        <FieldLabel required>{t('lms.consultation.teacher')}</FieldLabel>
        <Dropdown>
          <Controller name="teacherId" control={control} render={({ field }) => (
            <CommonInput
              key={`teacher-${activeDay}-${dropdownResetKey}`}
              type={INPUT_TYPES.dropdown}
              orientation="column"
              options={teacherOptions}
              value={teacherOptions.find((option) => option.value === field.value)}
              onChange={(option: any) => field.onChange(dropdownValue(option))}
              placeholder={t('lms.lessonsEditor.chooseTeacher')}
            />
          )} />
        </Dropdown>
        <Controller name="isInvitedTeacher" control={control} render={({ field }) => (
          <MagicBoxRow
            className="checkbox module_checkbox"
            checked={field.value}
            label={t('lms.consultation.invitedTeacher')}
            onClick={() => field.onChange(!field.value)}
            isTeamEdit
          />
        )} />
      </Field>

      <Field>
        <FieldLabel>{t('lms.lessonsEditor.groupsLabel')}</FieldLabel>
        <Chips>
          {courseGroups.map((group) => {
            const isSelected = (formData.selectedGroups || []).includes(group.groupId);
            return (
              <Chip
                key={group.groupId}
                type="button"
                $selected={isSelected}
                aria-pressed={isSelected}
                onClick={() => toggleGroup(group.groupId)}
              >
                <span>{getGroupName(group)}</span>
              </Chip>
            );
          })}
        </Chips>
      </Field>

      <FlexBox justify="flex-end">
        <BrgButton fullWidth variant={ButtonVariant.Filled} onClick={submit}>
          {t('lms.lessonsEditor.addLessonsToSchedule')}
        </BrgButton>
      </FlexBox>
    </ModalWrapper>
  );
};
